import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BasicDataView = ({ formData, setFormData, readOnly = false }) => {
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [materialGroups, setMaterialGroups] = useState([]);
  const [weightUnits, setWeightUnits] = useState([]);
  const [itemCategoryGroups, setItemCategoryGroups] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [salesOrgDivisionAssignments, setSalesOrgDivisionAssignments] = useState([]);
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search states for dropdowns
  const [uomSearch, setUomSearch] = useState('');
  const [mgSearch, setMgSearch] = useState('');
  const [wuSearch, setWuSearch] = useState('');
  const [icgSearch, setIcgSearch] = useState('');

  useEffect(() => {
    loadMasterData();
  }, []);

  // Get selected Sales Org from Sales View data
  const getSelectedSalesOrg = () => {
    const salesData = formData.sales_data || [];
    if (salesData.length > 0) {
      return salesData[0].sales_org_id;
    }
    return null;
  };

  // Filter divisions when Sales Org changes (from Sales View)
  useEffect(() => {
    const selectedSalesOrg = getSelectedSalesOrg();
    
    if (selectedSalesOrg && salesOrgDivisionAssignments.length > 0) {
      const assignedDivisionIds = salesOrgDivisionAssignments
        .filter(a => a.sales_org_id === selectedSalesOrg)
        .map(a => a.division_id);
      
      const filtered = divisions.filter(d => assignedDivisionIds.includes(d.division_id));
      setFilteredDivisions(filtered);
      
      // Clear division if it's not valid for the selected sales org
      if (formData.basic_data?.division && !assignedDivisionIds.includes(formData.basic_data.division)) {
        handleChange('division', '');
      }
    } else {
      setFilteredDivisions(divisions);
    }
  // eslint-disable-next-line
  }, [formData.sales_data, salesOrgDivisionAssignments, divisions]);

  const loadMasterData = async () => {
    try {
      const [uomRes, mgRes, wuRes, icgRes, divisionRes, assignmentsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/units-of-measure`),
        axios.get(`${BACKEND_URL}/api/material-groups`),
        axios.get(`${BACKEND_URL}/api/weight-units`),
        axios.get(`${BACKEND_URL}/api/item-category-groups`),
        axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`),
        axios.get(`${BACKEND_URL}/api/assignments/sales-org-division`)
      ]);

      setUnitsOfMeasure(uomRes.data || []);
      setMaterialGroups(mgRes.data || []);
      setWeightUnits(wuRes.data || []);
      setItemCategoryGroups(icgRes.data || []);
      setDivisions(divisionRes.data || []);
      setSalesOrgDivisionAssignments(assignmentsRes.data || []);
      setFilteredDivisions(divisionRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      basic_data: {
        ...prev.basic_data,
        [field]: value
      }
    }));
  };

  const filterOptions = (items, search, codeField, descField) => {
    if (!search) return items;
    return items.filter(item => 
      item[codeField].toLowerCase().includes(search.toLowerCase()) ||
      item[descField].toLowerCase().includes(search.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mb-3"></div>
        <p className="text-sm text-gray-600">Loading master data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">Basic Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Fundamental material characteristics and units of measure
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Material Description */}
        <div className="md:col-span-2">
          <Label>Material Description *</Label>
          <Input
            type="text"
            value={formData.basic_data?.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter material description"
            maxLength={40}
            required
            disabled={readOnly}
            className={`text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">
            Max 40 characters ({(formData.basic_data?.description || '').length}/40)
          </p>
        </div>

        {/* Base Unit of Measure */}
        <div>
          <Label>Base Unit of Measure *</Label>
          <div className="relative">
            <select
              className={`w-full mt-1 px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              value={formData.basic_data?.base_uom || ''}
              onChange={(e) => handleChange('base_uom', e.target.value)}
              required
              disabled={readOnly}
            >
              <option value="">Select Unit of Measure</option>
              {unitsOfMeasure.map(uom => (
                <option key={uom.unit_code} value={uom.unit_code}>
                  {uom.unit_code} - {uom.description}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Primary unit for this material (cannot be changed once stock exists)
          </p>
        </div>

        {/* Material Group */}
        <div>
          <Label>Material Group *</Label>
          <div className="relative">
            <select
              className={`w-full mt-1 px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              value={formData.basic_data?.material_group || ''}
              onChange={(e) => handleChange('material_group', e.target.value)}
              required
              disabled={readOnly}
            >
              <option value="">Select Material Group</option>
              {materialGroups.map(mg => (
                <option key={mg.material_group_id} value={mg.material_group_id}>
                  {mg.material_group_id} - {mg.description}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Group for categorization and reporting
          </p>
        </div>

        {/* Weight Unit */}
        <div>
          <Label>Weight Unit</Label>
          <div className="relative">
            <select
              className={`w-full mt-1 px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              value={formData.basic_data?.weight_unit || ''}
              onChange={(e) => handleChange('weight_unit', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select Weight Unit (Optional)</option>
              {weightUnits.map(wu => (
                <option key={wu.weight_unit_code} value={wu.weight_unit_code}>
                  {wu.weight_unit_code} - {wu.measurement_unit_text}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Unit for weight measurements (optional)
          </p>
        </div>

        {/* General Item Category Group */}
        <div>
          <Label>General Item Category Group</Label>
          <div className="relative">
            <select
              className={`w-full mt-1 px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              value={formData.basic_data?.gen_item_cater || ''}
              onChange={(e) => handleChange('gen_item_cater', e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select Item Category (Optional)</option>
              {itemCategoryGroups.map(icg => (
                <option key={icg.item_category_group_id} value={icg.item_category_group_id}>
                  {icg.item_category_group_id} - {icg.description}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            General item category for classification (optional)
          </p>
        </div>

        {/* Division - Derived from Sales Org selection in Sales View */}
        <div>
          <Label>Division</Label>
          <div className="relative">
            <select
              value={formData.basic_data?.division || ''}
              onChange={(e) => handleChange('division', e.target.value)}
              disabled={readOnly || !getSelectedSalesOrg()}
              className={`w-full px-3 py-2 pr-10 text-base border rounded-md appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                readOnly || !getSelectedSalesOrg() ? 'bg-gray-50 cursor-not-allowed text-gray-600' : 'text-gray-900'
              }`}
            >
              <option value="">
                {!getSelectedSalesOrg() ? 'Select Sales Org in Sales tab first' : 'Select Division'}
              </option>
              {filteredDivisions.map((div) => (
                <option key={div.division_id} value={div.division_id}>
                  {div.division_id} - {div.division_name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="h-4 w-4 fill-current text-gray-500" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {getSelectedSalesOrg() 
              ? `Showing divisions assigned to Sales Org: ${getSelectedSalesOrg()}` 
              : 'Please select Sales Org in Sales tab to see available divisions'}
          </p>
        </div>
      </div>

      {/* Required Fields Summary */}
      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">
            i
          </div>
          <div>
            <p className="text-sm font-medium text-orange-900 mb-1">Required Fields</p>
            <p className="text-sm text-orange-800">
              <strong>Material Description</strong>, <strong>Base Unit of Measure</strong>, and <strong>Material Group</strong> are mandatory. 
              All other fields are optional and can be filled based on your specific requirements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicDataView;